import { ActionContext, ActionTree } from 'vuex';
import { FlagsState } from '@/store/flags/types';
import { State } from '@/store/play-root-state';
import { stateLog } from '@/store/config/types';
import { fetchFeatureFlags, getAccountIdFromContext } from '@/boot/feature-flag-api';
import { DEFAULT_FEATURE_FLAGS } from './constants';

const log = stateLog('flags', 'actions');

type FlagsContext = ActionContext<FlagsState, State>;

const actions = <ActionTree<FlagsState, State>>{
    /**
     * Fetch feature flags from the API and update state
     *
     * IMPORTANT: This is called with force=true on every page load to ensure
     * users get the latest feature flag values from the server, even if they
     * changed during the session.
     *
     * @param force - Force fetch even if already fetched (default: false)
     *                On page boot, this is always true to get fresh values
     */
    async FETCH_FEATURE_FLAGS({
        commit, state, rootState,
    }: FlagsContext, force = false) {
        // Don't fetch if we've already fetched in this session (unless forced)
        // Note: On page boot, force=true is always passed to get fresh values
        if (state.featureFlagsFetched && !force) {
            log.debug('Feature flags already fetched, skipping (use force=true to override)');

            return;
        }

        // Skip API call if there are no flags to fetch
        if (DEFAULT_FEATURE_FLAGS.length === 0) {
            log.debug('No feature flags configured, skipping API call');
            // Mark as fetched since there's nothing to fetch
            commit('SET_FEATURE_FLAGS', { flags: {} });

            return;
        }

        try {
            log.info('Fetching feature flags from API', { force, flagCount: DEFAULT_FEATURE_FLAGS.length });

            // Get bearer token from auth session
            const bearerToken = rootState.auth?.session?.jwt;

            if (!bearerToken) {
                log.warn('No JWT token available in session, skipping feature flag fetch');
                log.info('Auth state:', {
                    hasSession: !!rootState.auth?.session,
                    hasJwt: !!bearerToken,
                });

                // DON'T set featureFlagsFetched=true when we skip due to missing JWT
                // This allows the fetch to happen later when JWT becomes available
                return;
            }

            // Get account ID from context (URL, store, etc.). Routes outside the Coaching Portal
            // carry the tenant as an :appId param, which the router has already put on the
            // session, so fall back to that before the placeholder.
            const accountId = getAccountIdFromContext() || rootState.auth?.session?.coreAppId || 'playweb';

            // Fetch feature flags with bearer token
            const flags = await fetchFeatureFlags(bearerToken, undefined, accountId, [...DEFAULT_FEATURE_FLAGS]);

            // Only cache if we got a valid response with authentication
            if (Object.keys(flags).length > 0) {
                // Handle nested response structure: API may return { featureFlags: { ... } }
                // or direct { flagName: value }
                const actualFlags = flags.featureFlags || flags;

                // Update flags in state (mutation will extract 'enabled' property)
                commit('SET_FEATURE_FLAGS', { flags: actualFlags });

                log.info('Feature flags set in store', actualFlags);
            } else {
                log.warn('Feature flag API returned empty response, will retry later');
            }
        } catch (error) {
            log.error('Failed to fetch feature flags', error);

            // DON'T set featureFlagsFetched=true on error
            // This allows retry on next attempt (e.g., after login)
        }
    },
};

export default actions;
